import {
  createFileRoute,
  useCanGoBack,
  useNavigate,
} from '@tanstack/react-router';
import { useSuspenseQuery } from '@tanstack/react-query';
import { getQuestionnaireResponseQueryOptions } from '@/api/queries/assessment';
import { BackButton } from '@/components/ui/backButton';
import { StickySubmissionStatus } from '@/components/assessment/stickySubmissionStatus';
import { FormPreviewSection } from '@/components/assessment/formPreviewSection';
import { ErrorComponent } from '@/components/generalError';
import { getPatientQueryOptions } from '@/api/queries/patients';
import { useTranslation } from 'react-i18next';
import { routes } from '@/types/routes';

export const Route = createFileRoute(
  '/_app/patients/$patientId/phases/$phaseId/assessments/$assessmentId/response',
)({
  component: RouteComponent,
  errorComponent: ErrorComponent,
});

function RouteComponent() {
  const { t } = useTranslation();
  const params = Route.useParams();
  const navigate = useNavigate();
  const canGoBack = useCanGoBack();
  const { patientId, phaseId, assessmentId } = params;

  const { data: patient } = useSuspenseQuery({
    ...getPatientQueryOptions(patientId),
    staleTime: 1000 * 60 * 60,
  });
  const activeSubject = patient.subjects?.find(subject => subject.isActive);

  if (!activeSubject) {
    throw new Error(t('errors.noActiveSubject', { ns: 'common' }));
  }
  const { data: form } = useSuspenseQuery(
    getQuestionnaireResponseQueryOptions({
      subjectId: activeSubject.id,
      phaseId,
      assessmentId,
    }),
  );

  return (
    <div className="relative flex flex-col flex-1">
      <div className="flex flex-col mb-10 gap-y-space-2xs">
        <div>
          <BackButton
            onClick={
              !canGoBack
                ? () =>
                  navigate({
                    to: routes.patientDetails,
                    params: { patientId },
                  })
                : undefined
            }
          />
        </div>
        <h1 className="text-text-primary-default text-3xl font-semibold">
          { form.questionnaire.name }
        </h1>
      </div>
      <div className="grid grid-cols-[1fr_355px] gap-x-space-lg mb-24">
        <FormPreviewSection result={ form } />
        <StickySubmissionStatus
          completedAt={ form.completedAt }
          completedByUser={ form.completedByUser }
        />
      </div>
    </div>
  );
}
